import React from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { CardEntityType } from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedChip } from "../ThemedChip";
import { ThemedCompactCard } from "./ThemedCompactCard";

export interface ICustomCardSectionRenderOptions {
  sectionKey: string;
  idx: number;
  entityType: CardEntityType;
  item: Record<string, any>;
  title: string;
  tintColor: string;
  textMuted: string;
  linksAlreadyRendered: boolean;
  onMarkLinksRendered: () => void;
}

/**
 * Renders an individual linked section defined in activeConfig.sections for ThemedCustomCard.
 * Linked sections rule:
 * - Contacts, Groups, Memories, Tags, Shares: Rendered as `<ThemedChip>`
 * - Media: Rendered as compact `<ThemedCompactCard entityType="media">`
 */
export function renderCustomCardSection({
  sectionKey,
  idx,
  entityType,
  item,
  title,
  tintColor,
  textMuted,
  linksAlreadyRendered,
  onMarkLinksRendered,
}: ICustomCardSectionRenderOptions): React.ReactNode {
  const uniqueKey = `custom-section-${sectionKey}-${idx}`;
  switch (sectionKey) {
    case "media": {
      // Suppress Media Highlights on media cards as the media asset is the primary content
      if (entityType === "media") return null;
      const mediaList = Object.values(item.mediaList || item.media || []);
      if (mediaList.length === 0) return null;
      return (
        <View key={uniqueKey} style={styles.sectionContainer}>
          <ThemedText type="stamp" style={[styles.sectionHeader, { color: textMuted }]}>
            MEDIA HIGHLIGHTS ({mediaList.length})
          </ThemedText>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.mediaScroll}>
            {mediaList.map((m: any, mIdx) => (
              <ThemedCompactCard
                key={`custom-media-${m.id || m.mediaId || mIdx}`}
                entityType="media"
                id={m.id || m.mediaId}
                title={m.title || m.name}
                thumbnailUrl={m.downloadUrl || m.thumbnailUrl || m.url}
                isOwner={m.isOwner}
                isFiveStar={m.isFiveStar || m.favouriteRating === 5}
              />
            ))}
          </ScrollView>
        </View>
      );
    }

    case "links": {
      if (linksAlreadyRendered) return null;
      onMarkLinksRendered();

      const contactsList = Object.values(item.contacts || item.contactsList || item.contactItems || []);
      const groupsList = Object.values(item.groups || item.groupsList || item.groupItems || []);
      const memoriesList = Object.values(item.memories || item.memoriesList || item.memoryItems || []);
      const placesList = Object.values(item.places || item.placesList || item.placeItems || []);
      const anchorsList = Object.values(item.anchors || item.anchorsList || item.anchorItems || []);
      const totalCount = contactsList.length + groupsList.length + memoriesList.length + placesList.length + anchorsList.length;
      if (totalCount === 0) return null;
      return (
        <View key={uniqueKey} style={styles.sectionContainer}>
          <View style={styles.sectionHeaderRow}>
            <Ionicons name="link-outline" size={11} color={tintColor} />
            <ThemedText type="stamp" style={[styles.sectionHeader, { color: textMuted }]}>
              LINKS ({totalCount})
            </ThemedText>
          </View>
          <View style={styles.chipRow}>
            {contactsList.map((c: any, cIdx) => (
              <ThemedChip
                key={`custom-contact-${c.id || c.contactId || cIdx}`}
                entityType="contact"
                id={c.id || c.contactId}
                label={c.displayName || c.name || "Contact"}
                thumbnailUrl={c.photoIconUrl || c.photoUrl}
                isOwner={c.isOwner}
              />
            ))}
            {groupsList.map((g: any, gIdx) => (
              <ThemedChip
                key={`custom-group-${g.id || g.groupId || gIdx}`}
                entityType="group"
                id={g.id || g.groupId}
                label={g.name || g.displayName || "Group"}
              />
            ))}
            {memoriesList.map((m: any, mIdx) => (
              <ThemedChip
                key={`custom-memory-${m.id || m.memoryId || mIdx}`}
                entityType="memory"
                id={m.id || m.memoryId}
                label={m.label || m.title || m.name || (m as any)?.memoryTitle || "Memory"}
                isFiveStar={m.favouriteRating === 5}
              />
            ))}
            {placesList.map((p: any, pIdx) => (
              <ThemedChip
                key={`custom-place-${p.id || p.placeId || pIdx}`}
                entityType="place"
                id={p.id || p.placeId}
                label={p.name || p.displayName || "Place"}
              />
            ))}
            {anchorsList.map((a: any, aIdx) => (
              <ThemedChip
                key={`custom-anchor-${a.id || a.anchorId || aIdx}`}
                entityType="anchor"
                id={a.id || a.anchorId}
                label={a.name || a.displayName || "Anchor"}
              />
            ))}
          </View>
        </View>
      );
    }

    case "tags": {
      const tagsList = Array.isArray(item.tagsList)
        ? item.tagsList
        : Object.values(item.tags || item.tagList || item.tagsList || {});
      if (tagsList.length === 0) return null;
      return (
        <View key={uniqueKey} style={styles.sectionContainer}>
          <ThemedText type="stamp" style={[styles.sectionHeader, { color: textMuted }]}>
            TAGS ({tagsList.length})
          </ThemedText>
          <View style={styles.chipRow}>
            {tagsList.map((t: any, tIdx) => (
              <ThemedChip
                key={`custom-tag-${t.id || t.tagId || tIdx}`}
                entityType="tag"
                tagType={t.tagType}
                id={t.id || t.tagId}
                label={t.value || t.name || t.label || "Tag"}
              />
            ))}
          </View>
        </View>
      );
    }

    case "shares": {
      const sharesList = Object.values(
        item.shares || item.sharesList || item.shareList || item.sharedWith || []
      );
      if (sharesList.length === 0) return null;
      return (
        <View key={uniqueKey} style={styles.sectionContainer}>
          <ThemedText type="stamp" style={[styles.sectionHeader, { color: textMuted }]}>
            SHARING ({sharesList.length})
          </ThemedText>
          <View style={styles.chipRow}>
            {sharesList.map((s: any, sIdx) => (
              <ThemedChip
                key={`custom-share-${s.id || s.shareId || sIdx}`}
                entityType="share"
                id={s.id || s.shareId}
                label={s.targetTitle || s.targetDisplayName || s.displayName || title}
                secondaryLabel={s.recipientName || s.recipientDisplayName}
              />
            ))}
          </View>
        </View>
      );
    }

    case "sharedItem":
    case "sharedItemPreview": {
      const sharedItem = item.sharedItem;
      if (!sharedItem) return null;
      return (
        <View key={uniqueKey} style={styles.sectionContainer}>
          <ThemedText type="stamp" style={[styles.sectionHeader, { color: textMuted }]}>
            SHARED ITEM PREVIEW
          </ThemedText>
          <ThemedCompactCard
            entityType={sharedItem.entityType || "memory"}
            id={sharedItem.id}
            title={sharedItem.title || sharedItem.name || "Shared Item"}
            date={sharedItem.date}
            thumbnailUrl={sharedItem.thumbnailUrl}
          />
        </View>
      );
    }

    default:
      return null;
  }
}

const styles = StyleSheet.create({
  sectionContainer: {
    gap: 2,
  },
  sectionHeaderRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  sectionHeader: {
    fontSize: 9,
    letterSpacing: 0.8,
  },
  mediaScroll: {
    flexDirection: "row",
    gap: 4,
    paddingVertical: 0,
  },
  chipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 4,
  },
});
